import { useEffect, useState } from "react";
import arcBridge from "frontend/js/arcBridge";
import GeocodingCache from "../utils/geocodingCache";

/**
 * Enhanced version of useLocationSearch with geocoding caching
 * This version reduces Google Maps API calls by using the caching system
 */
function useLocationSearchCached(inputRef, variantIds = []) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [hasManualSearch, setHasManualSearch] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const fetchStores = async ({ lat, lng, postcode = "", initialLoad = false }) => {
    const hasVariants = variantIds.length > 0;
    const requestParams = hasVariants
      ? {
          id: variantIds.join(","),
          ...(initialLoad ? {} : {
            postcode,
            delivery_lat: lat,
            delivery_lng: lng,
          }),
        }
      : {
          ...(initialLoad ? {} : {
            ...(postcode && { postcode }),
            ...(lat !== undefined && { delivery_lat: lat }),
            ...(lng !== undefined && { delivery_lng: lng }),
          }),
        };

    setLoading(true);

    try {
      let response;

      if (hasVariants) {
        response = await arcBridge.getClickAndCollectStore(requestParams);
        setStores(response);
      } else {
        response = await arcBridge.getLocations(requestParams);
        if (response?.stores?.length > 0 || initialLoad) {
          if (process.env.NODE_ENV === "development") {
            console.log("store response", response);
          }
          setStores(response?.stores || []);
        }
      }

      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching stores:", error);
      setStores([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Enhanced searchByQuery that uses geocoding cache
   */
  const searchByQuery = async (query, triggeredByAutocomplete = false) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setHasManualSearch(false);
      fetchStores({ initialLoad: true });
      return;
    }

    if (process.env.NODE_ENV === "development") {
      console.log("[useLocationSearchCached] Searching by query:", trimmedQuery, "triggeredByAutocomplete:", triggeredByAutocomplete);
    }

    try {
      // If triggered by autocomplete selection, try to skip geocoding if we have cached coordinates
      if (triggeredByAutocomplete) {
        const cachedResult = GeocodingCache.lookupGeocodeCache(trimmedQuery);
        if (cachedResult && cachedResult.location) {
          console.log("[useLocationSearchCached] Autocomplete: Using cached coordinates, skipping geocoding API");
          const { lat, lng } = cachedResult.location;
          const postcode = cachedResult.components?.postal_code || "";

          setUserCoords({ lat, lng });
          setHasManualSearch(true);
          fetchStores({ lat, lng, postcode });
          setErrorMessage("");
          return;
        }
      }

      // Use the cache system directly - no manual cache checking
      console.log("[useLocationSearchCached] Using cache system for geocoding");
      
      // Define the geocoding function for the cache system
      const geocodingFunction = async (queryToGeocode) => {
        // Try Google Geocoding API first
        try {
          let API_KEY = window?.theme?.settings?.google_maps_api_key;
          
          if (!API_KEY) {
            API_KEY = process.env.REACT_APP_GOOGLE_GEOCODING_API_KEY;
          }
          
          if (!API_KEY) {
            throw new Error('Google Geocoding API key not configured');
          }

          const isReverseGeocode = /^-?\d+\.?\d*,-?\d+\.?\d*$/.test(queryToGeocode.trim());
          
          const params = new URLSearchParams({
            [isReverseGeocode ? 'latlng' : 'address']: queryToGeocode,
            components: 'country:AU',
            key: API_KEY
          });

          const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?${params}`
          );

          if (!response.ok) {
            throw new Error(`Geocoding API error: ${response.status}`);
          }

          const data = await response.json();

          if (data.status !== 'OK' || !data.results || data.results.length === 0) {
            throw new Error(`No results found for: ${queryToGeocode}`);
          }

          const result = data.results[0];
          
          const components = {};
          result.address_components.forEach((component) => {
            component.types.forEach((type) => {
              components[type] = component.long_name;
            });
          });

          const locality = components.locality || 
            components.postal_town || 
            components.sublocality_level_1 || 
            '';

          return {
            formattedAddress: result.formatted_address,
            location: result.geometry?.location ? {
              lat: result.geometry.location.lat,
              lng: result.geometry.location.lng
            } : null,
            components: {
              postal_code: components.postal_code || '',
              locality: locality,
              administrative_area_level_1: components.administrative_area_level_1 || '',
              country: components.country || 'AU'
            }
          };
        } catch (apiError) {
          console.warn('[useLocationSearchCached] Geocoding API failed, falling back to Google Maps Geocoder:', apiError);
          
          // Fallback to Google Maps Geocoder
          if (!window.google?.maps) {
            throw new Error('Google Maps not available and Geocoding API failed');
          }

          return new Promise((resolve, reject) => {
            const geocoder = new window.google.maps.Geocoder();
            const options = {
              address: queryToGeocode,
              componentRestrictions: { country: "AU" }
            };

            geocoder.geocode(options, (results, status) => {
              if (status === "OK" && results[0]) {
                const result = results[0];
                
                const components = {};
                if (result.address_components) {
                  result.address_components.forEach((component) => {
                    component.types.forEach((type) => {
                      components[type] = component.long_name;
                    });
                  });
                }

                const locality = components.locality || 
                  components.postal_town || 
                  components.sublocality_level_1 || 
                  '';

                resolve({
                  formattedAddress: result.formatted_address,
                  location: {
                    lat: result.geometry.location.lat(),
                    lng: result.geometry.location.lng()
                  },
                  components: {
                    postal_code: components.postal_code || '',
                    locality: locality,
                    administrative_area_level_1: components.administrative_area_level_1 || '',
                    country: components.country || 'AU'
                  }
                });
              } else {
                reject(new Error(`Geocoding failed for query: ${queryToGeocode}. Status: ${status}`));
              }
            });
          });
        }
      };

      // Use the cache system to get or fetch the geocoding result
      const geocodeResult = await GeocodingCache.geocodeWithCache(trimmedQuery, geocodingFunction);

      // Extract coordinates and postcode from the geocoding result
      if (geocodeResult && geocodeResult.location) {
        const { lat, lng } = geocodeResult.location;
        const postcode = geocodeResult.components?.postal_code || "";

        setUserCoords({ lat, lng });
        setHasManualSearch(true);
        if (!triggeredByAutocomplete) {
          fetchStores({ lat, lng, postcode });
        }
        setErrorMessage("");
      } else {
        throw new Error('No valid location found in geocoding result');
      }

    } catch (error) {
      console.error("[useLocationSearchCached] Geocoding failed for query:", trimmedQuery, "Error:", error);
      setStores([]);
      setHasSearched(true);
      setErrorMessage("We couldn't find a location for that address. Please try a nearby suburb or postcode.");
    }
  };

  /**
   * Enhanced location search with reverse geocoding cache
   */
  const handleLocationSearch = async () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const { latitude, longitude } = position.coords;
      
      // Create reverse geocoding key for cache lookup
      const reverseKey = GeocodingCache.createReverseGeocodeKey(latitude, longitude);
      
      try {
        // Try cached reverse geocoding first
        const cachedResult = GeocodingCache.lookupGeocodeCache(reverseKey);
        
        let postcode = '';
        
        if (cachedResult && cachedResult.formattedAddress !== 'Unknown Location') {
          console.log('[useLocationSearchCached] Using cached reverse geocoding result');
          postcode = cachedResult.components?.postal_code || '';
        } else {
          // No cache hit or invalid cache, perform fresh reverse geocoding
          console.log('[useLocationSearchCached] Performing fresh reverse geocoding');
          
          const geocodingFunction = async () => {
            // Define geocoding API call for reverse geocoding
            let API_KEY = window?.theme?.settings?.google_maps_api_key;
            
            if (!API_KEY) {
              API_KEY = process.env.REACT_APP_GOOGLE_GEOCODING_API_KEY;
            }
            
            if (!API_KEY) {
              throw new Error('Google Geocoding API key not configured');
            }

            const params = new URLSearchParams({
              latlng: `${latitude},${longitude}`,
              key: API_KEY
            });

            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?${params}`
            );

            if (!response.ok) {
              throw new Error(`Reverse geocoding API error: ${response.status}`);
            }

            const data = await response.json();

            if (data.status !== 'OK' || !data.results || data.results.length === 0) {
              throw new Error(`No results found for coordinates: ${latitude},${longitude}`);
            }

            const result = data.results[0];
            
            const components = {};
            result.address_components.forEach((component) => {
              component.types.forEach((type) => {
                components[type] = component.long_name;
              });
            });

            const locality = components.locality || 
              components.postal_town || 
              components.sublocality_level_1 || 
              '';

            return {
              formattedAddress: result.formatted_address,
              location: { lat: latitude, lng: longitude },
              components: {
                postal_code: components.postal_code || '',
                locality: locality,
                administrative_area_level_1: components.administrative_area_level_1 || '',
                country: components.country || 'AU'
              }
            };
          };

          try {
            const freshResult = await GeocodingCache.geocodeWithCache(reverseKey, geocodingFunction);
            postcode = freshResult?.components?.postal_code || '';
          } catch (geocodeError) {
            console.warn('[useLocationSearchCached] Reverse geocoding failed:', geocodeError);
            // Continue without postcode
          }
        }

        setUserCoords({ lat: latitude, lng: longitude });
        setHasManualSearch(false);
        fetchStores({ lat: latitude, lng: longitude, postcode });
        setErrorMessage("");

      } catch (error) {
        console.error('[useLocationSearchCached] Location processing failed:', error);
        // Fallback: just use coordinates without reverse geocoding
        setUserCoords({ lat: latitude, lng: longitude });
        setHasManualSearch(false);
        fetchStores({ lat: latitude, lng: longitude });
        setErrorMessage("");
      }
        
    } catch (geolocationError) {
      console.error('[useLocationSearchCached] Geolocation failed:', geolocationError);
      alert("We couldn't access your location. Please try again or enter a suburb.");
      setHasSearched(true);
      setErrorMessage("We couldn't access your location. Please try again or enter a suburb.");
    }
  };

  return {
    handleLocationSearch,
    searchByQuery,
    fetchStores,
    stores,
    loading,
    hasSearched,
    userCoords,
    hasManualSearch,
    errorMessage,
  };
}

export default useLocationSearchCached;
